/**
 * «Рассылки» → «Отзыв о примерке»: рассылка «Как прошла примерка» ушедшим без брони — по дню
 * ОТПРАВКИ, в какой канал сколько сообщений ушло и какие кнопки нажали («Хорошо» / «Плохо»).
 * Канал выбирается по номеру клиента: бесплатный (боты, площадки) раньше платного шаблона
 * WhatsApp. Спека `docs/specs/2026-10-06-mailing-fitting-design.md`; числа — как у
 * `prokat-runner mailing-fitting --stats`.
 */
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router";

import { DateRangePicker } from "@/components/DateRangePicker";
import { iso, plusDays } from "@/lib/dates";
import { type FittingChannel, type FittingCounts, loadMailingFitting } from "@/lib/mailingFitting";

const CHANNEL: Record<FittingChannel, { label: string; hint: string }> = {
  telegram: { label: "Бот Telegram", hint: "бесплатно: клиент запустил @unirenterbot" },
  max: { label: "Бот MAX", hint: "бесплатно: клиент запустил бота MAX" },
  vk: { label: "ВКонтакте", hint: "бесплатно: клиент писал сообществу за последние 90 дней; без кнопок" },
  avito: { label: "Авито", hint: "бесплатно: клиент писал в Авито за последние 90 дней; без кнопок" },
  instagram: { label: "Instagram", hint: "бесплатно: клиент писал меньше суток назад; без кнопок" },
  whatsapp: { label: "WhatsApp", hint: "платный шаблон WABA — когда бесплатного канала нет" },
};
const WEEKDAY = ["вс", "пн", "вт", "ср", "чт", "пт", "сб"];
const ZERO: FittingCounts = { sent: 0, good: 0, bad: 0 };

function pct(a: number, b: number): string {
  return a && b ? `${Math.round((a * 100) / b)}%` : "";
}

function dm(day: string): string {
  return `${day.slice(8, 10)}.${day.slice(5, 7)}`;
}

/** Три ячейки счёта: отправлено · «Хорошо» · «Плохо»; доли оценок — от отправленных. */
function Cells({ c, last }: { c: FittingCounts; last?: boolean }) {
  const td = "whitespace-nowrap px-3 py-1.5 tabular-nums";
  const mark = (v: number, tone: string) => (
    <>
      <span className={`text-[14px] font-semibold ${v ? tone : "text-fg-faint"}`}>{v}</span>
      {v > 0 && <span className="ml-1.5 text-[12px] text-fg-muted">{pct(v, c.sent)}</span>}
    </>
  );
  return (
    <>
      <td className={td}>
        <span className={`text-[14px] font-semibold ${c.sent ? "text-fg" : "text-fg-faint"}`}>{c.sent}</span>
      </td>
      <td className={td}>{mark(c.good, "text-ok")}</td>
      <td className={`${td} ${last ? "" : "border-r border-border"}`}>{mark(c.bad, "text-err")}</td>
    </>
  );
}

export function FittingTab() {
  const [params, setParams] = useSearchParams();
  const today = new Date();
  const from = params.get("from") || iso(plusDays(today, -29));
  const to = params.get("to") || iso(today);
  const set = (patch: Record<string, string>) => {
    const out = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v) out.set(k, v);
      else out.delete(k);
    }
    setParams(out, { replace: true });
  };
  const q = useQuery({ queryKey: ["mailing-fitting", from, to], queryFn: () => loadMailingFitting(from, to) });
  const th = "whitespace-nowrap px-3 py-2 font-medium";
  const r = q.data;
  // Колонки — только каналы, куда за период что-то ушло: шесть пустых троек таблицу не красят.
  const channels = r ? r.channels.filter((k) => r.total.channels[k].sent > 0) : [];
  const all = r?.total.all ?? ZERO;

  return (
    <section className="flex flex-col gap-3" data-annot="отзыв о примерке">
      <div className="flex flex-wrap items-center gap-3" data-annot="период отзыва о примерке">
        <DateRangePicker label="Отправлено" from={from} to={to} onChange={(a, b) => set({ from: a, to: b })} />
      </div>
      {q.isPending && <div className="text-[12.5px] text-fg-muted">Считаю рассылку…</div>}
      {q.isError && <div className="text-[12.5px] text-err">Не удалось получить отчёт: {String(q.error.message)}</div>}
      {r && (
        <div className="flex flex-wrap gap-x-8 gap-y-2 text-[12.5px] text-fg-muted" data-annot="итог отзыва о примерке">
          <span>
            Отправлено <b className="text-[15px] text-fg">{all.sent}</b>
          </span>
          <span title="боты Telegram и MAX, ВКонтакте, Авито, Instagram">
            бесплатно <b className="text-[15px] text-ok">{r.total.free}</b> {pct(r.total.free, all.sent)}
          </span>
          <span title="шаблон WABA — когда бесплатного канала у клиента нет">
            платным шаблоном <b className="text-[15px] text-fg">{r.total.paid}</b> {pct(r.total.paid, all.sent)}
          </span>
          <span>
            «Хорошо» <b className={`text-[15px] ${all.good ? "text-ok" : "text-fg-faint"}`}>{all.good}</b> {pct(all.good, all.sent)}
          </span>
          <span>
            «Плохо» <b className={`text-[15px] ${all.bad ? "text-err" : "text-fg-faint"}`}>{all.bad}</b> {pct(all.bad, all.sent)}
          </span>
        </div>
      )}
      {r && r.days.length === 0 && (
        <div className="rounded-[var(--radius-card)] border border-border bg-bg-subtle px-4 py-3 text-[12.5px] text-fg-muted">
          За период рассылка никому не уходила.
        </div>
      )}
      {r && r.days.length > 0 && (
        <section className="rounded-[var(--radius-card)] border border-border bg-bg-subtle" data-annot="отзыв о примерке по дням">
          <div className="overflow-x-auto">
            <table className="w-auto text-[12.5px]">
              <thead className="text-left text-[11.5px] text-fg-faint">
                <tr>
                  <th className={`${th} pl-4`} rowSpan={2}>
                    День
                  </th>
                  <th className={`${th} border-r border-border font-normal`} colSpan={3}>
                    Все каналы
                  </th>
                  {channels.map((k, i) => (
                    <th
                      key={k}
                      className={`${th} font-normal ${i + 1 < channels.length ? "border-r border-border" : ""}`}
                      colSpan={3}
                      title={CHANNEL[k].hint}
                    >
                      {CHANNEL[k].label}
                    </th>
                  ))}
                </tr>
                <tr>
                  {["all", ...channels].map((k, i) => (
                    <FittingHead key={k} th={th} last={i === channels.length} />
                  ))}
                </tr>
              </thead>
              <tbody>
                {r.days.map((d) => {
                  const wd = new Date(`${d.day}T12:00:00`).getDay();
                  return (
                    <tr
                      key={d.day}
                      className={`border-t border-border hover:bg-bg-hover ${wd === 0 || wd === 6 ? "bg-bg" : ""}`}
                      data-annot={`день ${d.day}`}
                    >
                      <td className="whitespace-nowrap py-1.5 pl-4 pr-3 tabular-nums">
                        {dm(d.day)} {WEEKDAY[wd]}
                      </td>
                      <Cells c={d.total} />
                      {channels.map((k, i) => (
                        <Cells key={k} c={d.channels[k] ?? ZERO} last={i + 1 === channels.length} />
                      ))}
                    </tr>
                  );
                })}
                <tr className="border-t-2 border-border font-medium" data-annot="итого за период">
                  <td className="py-2 pl-4 pr-3 text-fg-muted">
                    Итого {dm(r.from)} – {dm(r.to)}
                  </td>
                  <Cells c={all} />
                  {channels.map((k, i) => (
                    <Cells key={k} c={r.total.channels[k]} last={i + 1 === channels.length} />
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
          <div className="border-t border-border px-4 py-2 text-[11.5px] text-fg-faint">
            День — дата отправки сообщения «Как прошла примерка». Оценка относится ко дню своей рассылки, а не ко дню
            нажатия; считается первое нажатие. Доли — от отправленных в канал. Канал выбирается по номеру клиента:
            бесплатный раньше платного WhatsApp. Кнопки есть в ботах Telegram и MAX и в шаблоне WhatsApp.
          </div>
        </section>
      )}
    </section>
  );
}

function FittingHead({ th, last }: { th: string; last: boolean }) {
  return (
    <>
      <th className={th} title="сообщений отправлено">
        Отпр.
      </th>
      <th className={th} title="нажали «Хорошо»; доля — от отправленных">
        «Хорошо»
      </th>
      <th className={`${th} ${last ? "" : "border-r border-border"}`} title="нажали «Плохо»; доля — от отправленных">
        «Плохо»
      </th>
    </>
  );
}
